import type { DataProvider } from "ra-core";
import { makePersonsAdapter } from "./PersonAdapter";
import type { PersonService } from "@/domain/port/PersonService";
import type { ClientService } from "@/domain/port/ClientService";
import { makeClientAdapter } from "./ClientAdapter";
import type { ConstructionSiteService } from "@/domain/port/ConstructionSiteService";
import { makeConstructionSiteAdapter } from "./ConstructionSiteAdapter";
import type { JobService } from "@/domain/port/JobService";
import { makeJobAdapter } from "./JobAdapter";
import type { AddressService } from "@/domain/port/AddressService";
import { makeAddressAdapter } from "./AddressAdapter";
import type { AssigneesService } from "@/domain/port/AssigneesService";
import { makeAssigneesAdapter } from "./AssigneesAdapter";

type Instrument = (info: {
  op: string;
  resource: string;
  ms: number;
  err?: unknown;
}) => void;

export function makeDataProvider(deps: {
  persons: PersonService;
  clients: ClientService;
  constructionSites: ConstructionSiteService;
  jobs: JobService;
  addresses: AddressService;
  assignees: AssigneesService;
  instrument?: Instrument;
}): DataProvider {
  const persons = makePersonsAdapter(deps.persons);
  const clients = makeClientAdapter(deps.clients);
  const constructionSites = makeConstructionSiteAdapter(deps.constructionSites);
  const jobs = makeJobAdapter(deps.jobs);
  const addresses = makeAddressAdapter(deps.addresses);
  const assignees = makeAssigneesAdapter(deps.assignees);

  const wrap =
    <F extends Function>(resource: string, op: string, fn: F) =>
    async (...args: any[]) => {
      const t0 = performance.now();
      try {
        const res = await (fn as any)(resource, ...args);
        deps.instrument?.({
          op,
          resource,
          ms: Math.round(performance.now() - t0),
        });
        return res;
      } catch (err) {
        deps.instrument?.({
          op,
          resource,
          ms: Math.round(performance.now() - t0),
          err,
        });
        throw err;
      }
    };

  return {
    getList: (resource, params) => {
      switch (resource) {
        case "persons":
          return wrap(resource, "getList", persons.getList)(params);
        case "clients":
          return wrap(resource, "getList", clients.getList)(params);
        case "constructionSites":
          return wrap(resource, "getList", constructionSites.getList)(params);
        case "jobs":
          return wrap(resource, "getList", jobs.getList)(params);
        case "addresses":
          return wrap(resource, "getList", addresses.getList)(params);
        case "assignees":
          return wrap(resource, "getList", assignees.getList)(params);
        default:
          return Promise.reject(new Error(`Unknown resource: ${resource}`));
      }
    },
    getOne: (resource, params) => {
      switch (resource) {
        case "clients":
          return wrap(resource, "getOne", clients.getOne)(params);
        case "addresses":
          return wrap(resource, "getOne", addresses.getOne)(params);
        case "constructionSites":
          return wrap(resource, "getOne", constructionSites.getOne)(params);
        case "jobs":
          return wrap(resource, "getOne", jobs.getOne)(params);
        case "persons":
          return wrap(resource, "getOne", persons.getOne)(params);
        /*optional
        case "assignees":
          return wrap(resource, "getOne", assignees.getOne)(params);

      */
        default:
          return Promise.reject(new Error(`Unknown resource: ${resource}`));
      }
    },
    getMany: (resource, params) => {
      switch (resource) {
        case "addresses":
          return wrap(resource, "getMany", addresses.getMany)(params);
        case "clients":
          return wrap(resource, "getMany", clients.getMany)(params);
        case "constructionSites":
          return wrap(resource, "getMany", constructionSites.getMany)(params);
        case "assignees":
          return wrap(resource, "getMany", assignees.getMany)(params);
        default:
          return Promise.reject(new Error(`Unknown resource: ${resource}`));
      }
    },
    getManyReference: async () => ({ data: [], total: 0 }),
    create: (resource, params) => {
      switch (resource) {
        case "persons":
          return wrap(resource, "create", persons.create)(params);
        case "clients":
          return wrap(resource, "create", clients.create)(params);
        case "constructionSites":
          return wrap(resource, "create", constructionSites.create)(params);
        case "jobs":
          return wrap(resource, "create", jobs.create)(params);
        default:
          return Promise.reject(new Error(`Unknown resource: ${resource}`));
      }
    },
    update: (resource, params) => {
      switch (resource) {
        case "clients":
          return wrap(resource, "update", clients.update)(params);
        case "addresses":
          return wrap(resource, "update", addresses.update)(params);
        case "constructionSites":
          return wrap(resource, "update", constructionSites.update)(params);
        case "jobs":
          return wrap(resource, "update", jobs.update)(params);
        case "persons":
          return wrap(resource, "update", persons.update)(params);
        default:
          return Promise.reject(new Error(`Unknown resource: ${resource}`));
      }
    },
    updateMany: async () => ({ data: [] }),
    delete: (resource, params) => {
      switch (resource) {
        case "clients":
          return wrap(resource, "delete", clients.delete)(params);
        case "addresses":
          return wrap(resource, "delete", addresses.delete)(params);
        case "constructionSites":
          return wrap(resource, "delete", constructionSites.delete)(params);
        case "jobs":
          return wrap(resource, "delete", jobs.delete)(params);
        case "persons":
          return wrap(resource, "delete", persons.delete)(params);
        default:
          return Promise.reject(new Error(`Unknown resource: ${resource}`));
      }
    },
    deleteMany: async () => ({ data: [] }),
  };
}
